import {useState} from 'react';
import styled from 'styled-components';
import {Button, EMPHASIS} from '@forecast-it/design-system';
import {useAcceptSuggestedAction} from '../hooks/useAcceptSuggestedAction';
import {useForecastProjects} from '../hooks/useForecastProjects';

export interface SuggestedActionForPanel {
	id: string;
	title: string;
	rationale: string | null;
	suggestedDueDate: string | null;
	assignee: {id: string; fullName: string | null; initials: string | null} | null;
}

interface Props {
	ticketId: string;
	action: SuggestedActionForPanel;
	onCancel: () => void;
	onCompleted: () => void;
}

const Panel = styled.div`
	margin-top: 8px;
	padding: 12px;
	background: #ffffff;
	border: 1px solid #e9d5ff;
	border-radius: ${p => p.theme.sys.size.borderradius['3']};
`;
const Field = styled.label`
	display: block;
	margin-bottom: 10px;
	${p => p.theme.sys.font?.detail?.['1']}
	color: ${p => p.theme.sys.color.text.content.auxiliary};
`;
const Input = styled.input`
	display: block;
	width: 100%;
	margin-top: 4px;
	padding: 6px 8px;
	${p => p.theme.sys.font?.body?.['2']}
	color: ${p => p.theme.sys.color.text.content.main};
	border: 1px solid ${p => p.theme.sys.color.border.decorative};
	border-radius: ${p => p.theme.sys.size.borderradius['3']};
`;
const Select = styled.select`
	display: block;
	width: 100%;
	margin-top: 4px;
	padding: 6px 8px;
	${p => p.theme.sys.font?.body?.['2']}
	color: ${p => p.theme.sys.color.text.content.main};
	background: #ffffff;
	border: 1px solid ${p => p.theme.sys.color.border.decorative};
	border-radius: ${p => p.theme.sys.size.borderradius['3']};
`;
const TextArea = styled.textarea`
	display: block;
	width: 100%;
	margin-top: 4px;
	padding: 6px 8px;
	min-height: 60px;
	${p => p.theme.sys.font?.body?.['2']}
	color: ${p => p.theme.sys.color.text.content.main};
	border: 1px solid ${p => p.theme.sys.color.border.decorative};
	border-radius: ${p => p.theme.sys.size.borderradius['3']};
`;
const AssigneeRow = styled.label`
	display: flex;
	align-items: center;
	gap: 6px;
	margin-bottom: 10px;
	${p => p.theme.sys.font?.detail?.['1']}
	color: ${p => p.theme.sys.color.text.content.auxiliary};
`;
const Actions = styled.div`
	display: flex;
	gap: 8px;
`;

export function AcceptSuggestionPanel({ticketId, action, onCancel, onCompleted}: Props) {
	const [title, setTitle] = useState(action.title);
	const [dueDate, setDueDate] = useState(action.suggestedDueDate ?? '');
	const [description, setDescription] = useState(action.rationale ?? '');
	const [includeAssignee, setIncludeAssignee] = useState(action.assignee != null);
	// Every task must belong to a project, and a ticket carries no project of its
	// own to default to — so the user picks one here. Starts empty; Save stays
	// disabled until a project is chosen.
	const [projectId, setProjectId] = useState('');
	const {projects, loading: projectsLoading} = useForecastProjects();
	const {accept, inFlight} = useAcceptSuggestedAction({onCompleted});

	const onSave = () => {
		if (!projectId) return;
		accept({
			ticketId,
			actionId: action.id,
			title: title.trim(),
			projectId,
			assignedPersonIds: includeAssignee && action.assignee ? [action.assignee.id] : [],
			dueDate: dueDate || null,
			description: description || null,
		});
	};

	return (
		<Panel>
			<Field>
				Title
				<Input aria-label="Title" value={title} onChange={e => setTitle(e.target.value)} />
			</Field>
			<Field>
				Project
				<Select
					aria-label="Project"
					value={projectId}
					onChange={e => setProjectId(e.target.value)}
					disabled={projectsLoading}
				>
					<option value="" disabled>
						Select a project…
					</option>
					{projects.map(p => (
						<option key={p.id} value={p.id}>
							{p.name}
						</option>
					))}
				</Select>
			</Field>
			<Field>
				Due date
				<Input aria-label="Due date" type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} />
			</Field>
			<Field>
				Description
				<TextArea aria-label="Description" value={description} onChange={e => setDescription(e.target.value)} />
			</Field>
			{action.assignee && (
				<AssigneeRow>
					<input
						type="checkbox"
						aria-label={`Assign to ${action.assignee.fullName ?? 'suggested assignee'}`}
						checked={includeAssignee}
						onChange={e => setIncludeAssignee(e.target.checked)}
					/>
					<span>{action.assignee.fullName ?? 'Suggested assignee'}</span>
				</AssigneeRow>
			)}
			<Actions>
				<Button emphasis={EMPHASIS.HIGH} disabled={inFlight || title.trim() === '' || !projectId} onPress={onSave}>
					Save
				</Button>
				<Button emphasis={EMPHASIS.LOW} onPress={onCancel}>
					Cancel
				</Button>
			</Actions>
		</Panel>
	);
}
